import { Button, Center, Flex, Input, InputGroup, Text } from '@chakra-ui/react';
import {
  UserItem,
  CloseIcon,
  SearchIcon,
  VoiceEnabledIcon,
  VoiceDisabledIcon,
  VideoEnabledIcon,
  VideoDisabledIcon,
  IphoneDeviceIcon,
  DesktopDeviceIcon,
  ScreenShareIcon,
} from '@halo/ui-chat-react';
import { Drawer, MyTrans } from '@halo/ui-react';
import type { MediaDeviceItem } from '@halo/chat-sdk';
import { MediaSDKMediaType, StateEnum } from '@halo/chat-sdk';
import { useAppStore } from '@halo/chat-sdk-react';
import { useActionsStore } from '@/store/actions';

export interface CallMembersProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onInvite: () => void;
  members: any[];
  callMembers?: any[];
}

export const CallMembers = ({
  open,
  onClose,
  onInvite,
  members,
  callMembers,
}: CallMembersProps) => {
  const { t } = useTranslation();
  const client = useAppStore((store) => store.client);
  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);

  const [memberDeviceList, setMemberDeviceList] = useState<MediaDeviceItem[]>([]);
  const [search, setSearch] = useState('');

  const filteredMembers = useMemo(() => {
    return members.filter((member) => member.name?.toLowerCase().includes(search?.toLowerCase()));
  }, [members, search]);

  const onMemberAvatarClick = useCallback(
    async (userId: string) => {
      fetchProfileUser(userId);
    },
    [fetchProfileUser]
  );

  useEffect(() => {
    if (members.length && open) {
      client?.Api.media
        .getUserMediaDeviceList({ list: members.map((member) => member.user_id) })
        .then((data) => {
          setMemberDeviceList(data.list);
        });
    }
  }, [client?.Api.media, open, members]);

  return (
    <Drawer.Root open={open} onOpenChange={(e) => onClose(e.open)} lazyMount unmountOnExit>
      <Drawer.Positioner pos="absolute" boxSize="full" zIndex={1100}>
        <Drawer.Content bg="bg.subtle" w="456px" flex="0 0 auto" maxW="none">
          <Drawer.Header justifyContent="space-between" py="2.5" px="4" pt="5">
            <Text fontSize="md" fontWeight="semibold">
              <MyTrans i18nKey="chat.groupMembersTitle" />
            </Text>
            <Drawer.CloseTrigger asChild pos="relative" right="0" top="0">
              <Center fontSize="md" cursor="pointer" color="fg.subtle">
                <CloseIcon />
              </Center>
            </Drawer.CloseTrigger>
          </Drawer.Header>
          <Drawer.Body className="scrollHover" px="4" py="2.5">
            <Flex gap="2">
              <InputGroup
                startElement={
                  <Center flexShrink="0" fontSize="md">
                    <SearchIcon />
                  </Center>
                }
              >
                <Input
                  size="sm"
                  placeholder={t('filter.searchPlaceholder')}
                  bg="bg"
                  borderRadius="xl"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>

              <Button
                size="sm"
                bg="bg"
                variant="outline"
                borderRadius="xl"
                color="#1A1A1A"
                onClick={onInvite}
              >
                <MyTrans i18nKey="buttons.invite" />
              </Button>
            </Flex>
            {filteredMembers.map((member, index) => {
              const [userId] = member.user_id?.split('.') || [];
              const isDesktopDevice =
                memberDeviceList?.find((item) => item?.user_device === member.user_id)
                  ?.device_type === 3;

              const hasOtherDevice = callMembers?.find(
                (item) => item?.user_id !== member.user_id && item?.user_id?.includes(userId)
              );

              const hasShareScreen = callMembers?.find(
                (item) => item.user_id === member.user_id && item?.screenSharing
              );

              const memberIcon = hasOtherDevice ? (
                isDesktopDevice ? (
                  <DesktopDeviceIcon />
                ) : (
                  <IphoneDeviceIcon />
                )
              ) : (
                <></>
              );

              const shareScreenIcon = hasShareScreen ? <ScreenShareIcon /> : <></>;
              return (
                <UserItem
                  key={member.user_id}
                  {...member}
                  rippleEffect={false}
                  hoverable={false}
                  name={
                    <Center gap="1">
                      {member?.state === StateEnum.DELETE_ACCOUNT
                        ? t('chat.deleteAccountText')
                        : member?.name}
                      {memberIcon}
                      {shareScreenIcon}
                    </Center>
                  }
                  onAvatarClick={() => onMemberAvatarClick?.(userId)}
                  borderColor={
                    index === filteredMembers.length - 1 ? 'transparent' : 'border.subtle'
                  }
                  endElement={
                    <Center gap="3.5">
                      {member?.callActionMap?.[MediaSDKMediaType.MICROPHONECAMERA_AUDIO] ? (
                        <VoiceEnabledIcon />
                      ) : (
                        <VoiceDisabledIcon />
                      )}
                      {member?.callActionMap?.[MediaSDKMediaType.MICROPHONECAMERA_VIDEO] ? (
                        <VideoEnabledIcon />
                      ) : (
                        <VideoDisabledIcon />
                      )}
                    </Center>
                  }
                />
              );
            })}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
};
